import React, { useRef, useEffect, useState } from 'react'
import classNames from 'classnames'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'

export type AnimationType = {
  filename?: string
  url?: string
  extension?: string
}

export interface VideoAnimationTypes {
  animation?: AnimationType
  image?: any
  autoPlay?: boolean
  classes?: string
  containerClasses?: string
  corners?: boolean
  fullBleed?: boolean
  loop?: boolean
  pauseVideo?: boolean
  playDelay?: number
  playVideo?: boolean
  previewImage?: any
  progressWrapperClasses?: string
  showPlayButton?: boolean
  showProgressBar?: boolean
  componentStyleContext?: string
}

const VideoAnimation = ({
  animation,
  image,
  autoPlay,
  classes = '',
  containerClasses = '',
  corners,
  fullBleed,
  loop,
  pauseVideo,
  playDelay = 0,
  playVideo,
  previewImage,
  progressWrapperClasses,
  showPlayButton,
  showProgressBar,
  componentStyleContext
}: VideoAnimationTypes) => {
  const pageContext = usePageContext()
  const VideoRef = useRef(null)
  const [hideImage, setHideImage] = useState<boolean>(false)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [transitionDuration, setTransitionDuration] = useState<number>(0)

  useEffect(() => {
    if (window.innerWidth < 992) {
      window.addEventListener('scroll', () => {
        playInView()
      })
    }
    VideoRef.current.addEventListener('play', () => {
      setHideImage(true)
    })
  }, [])

  useEffect(() => {
    if (isPlaying) {
      handlePlay()
    }
  }, [isPlaying])

  // Allow for parent component to control play/pause state
  useEffect(() => {
    if (playVideo && !isPlaying) {
      // Delay the video play if needed, depending on condition from parent component
      setTimeout(() => {
        handlePlay()
      }, playDelay)
    }
    if (pauseVideo && isPlaying) {
      handlePause()
    }
  }, [pauseVideo, playVideo])

  useEffect(() => {
    if (autoPlay) {
      handlePlay()
    }
  }, [autoPlay])

  useEffect(() => {
    // We need to handle loop this way when we're showing the progress bar, in order to properly reset state and loop the progress bar with the video
    if (loop && showProgressBar && VideoRef.current) {
      VideoRef.current.addEventListener('ended', () => {
        setTransitionDuration(0)
        setProgress(0)
        VideoRef.current.play()
        setIsPlaying(true)
        // Need the timeout to prevent race condition setting progress to 100 before the video restarts, which prevents the progress bar from resetting
        setTimeout(() => {
          handlePlay()
        }, 100)
      })
    }
  }, [])

  const handlePlay = () => {
    // This handles the progress bar state when we play the video
    // By setting the transition time to the remaining time of the video and setting width to 100%, it ensures a smooth progress flow from start to finish
    if (showProgressBar) {
      const { currentTime, duration } = VideoRef.current
      let transitionTime = duration
      // This adjusts the transition to account for any time elapsed, if the video had previously been paused
      if (currentTime > 0 || transitionTime === 0) {
        transitionTime = duration - currentTime
      }
      setTransitionDuration(transitionTime)
      setProgress(100)
    }
    VideoRef.current.play()
    setIsPlaying(true)
  }

  const handlePause = () => {
    if (showProgressBar) {
      const { currentTime, duration } = VideoRef.current
      setTransitionDuration(0)
      setProgress((currentTime / duration) * 100)
    }
    VideoRef.current.pause()
    setIsPlaying(false)
  }

  const playInView = () => {
    const rect = VideoRef.current.getBoundingClientRect()
    if (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <=
        (window.innerWidth || document.documentElement.clientWidth) &&
      isPlaying === false
    ) {
      handlePlay()
    }
  }

  const videoPlay = () => {
    if (!autoPlay) {
      handlePlay()
    }
  }

  const videoPlayToggle = () => {
    if (!isPlaying) {
      handlePlay()
    } else {
      handlePause()
    }
  }
  const containerCss = classNames(containerClasses, 'relative', {
    'h-full': componentStyleContext !== 'landing-page' && !fullBleed,
    'w-full': componentStyleContext === 'landing-page' || fullBleed
  })

  const progressWrapClasses = classNames(
    'absolute inset-0 overflow-hidden',
    progressWrapperClasses
  )
  const videoClasses = classNames(classes, {
    'w-full':
      classes === '' || componentStyleContext === 'landing-page' || fullBleed,
    'cursor-pointer': showPlayButton
  })
  const previewImageClasses = classNames(
    'object-cover h-full w-full absolute top-0 bottom-0 right-0 left-0',
    {
      'rounded-lg lg:rounded-md': corners,
      block: !hideImage,
      hidden: hideImage,
      'cursor-pointer': showPlayButton
    }
  )
  const playButtonClasses = classNames(
    'relative rounded-full bg-gray-125/[.5] w-full h-full before:h-[24px] before:w-[24px] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
    {
      'before:content-icon-pause': isPlaying,
      'before:content-icon-play-fill': !isPlaying
    }
  )
  const renderResponsiveAnimation = () => {
    if (
      image &&
      !animation &&
      image?.desktop?.file?.contentType === 'video/mp4'
    ) {
      const desktop = image.desktop
      const tablet = image.tablet || desktop
      const mobile = image.mobile || desktop
      let sources = (
        <source media='(max-width: 767px)' src={mobile?.file?.url} />
      )
      if (tablet?.file?.url) {
        sources = (
          <>
            {sources}
            <source media='(max-width: 991px)' src={tablet?.file?.url} />
          </>
        )
      }
      if (image.desktop.file.url) {
        sources = (
          <>
            {sources}
            <source media='(min-width: 992px)' src={image.desktop.file.url} />
          </>
        )
      }
      return <>{sources}</>
    }
  }

  return (
    <div className={containerCss}>
      {showProgressBar && (
        <div className={progressWrapClasses}>
          <div
            style={{
              width: `${progress}%`,
              transitionDuration: `${transitionDuration}s`
            }}
            className='h-[4px] bg-blue-50 z-20 transition-all ease-linear'
          />
        </div>
      )}
      {showPlayButton && (
        <div className='absolute bottom-2 left-2 w-[42px] h-[42px]'>
          <button className={playButtonClasses} />
        </div>
      )}
      <video
        autoPlay={autoPlay}
        className={videoClasses}
        data-testid='video-animation'
        loop={!showProgressBar && loop}
        muted
        onClick={showPlayButton ? videoPlayToggle : videoPlay}
        onMouseEnter={!showPlayButton ? videoPlay : null}
        playsInline
        preload='metadata'
        ref={VideoRef}
      >
        {renderResponsiveAnimation()}
        {animation?.url && <source src={animation.url} />}
        {animation?.filename && (
          <source
            src={`https://d2w1ef2ao9g8r9.cloudfront.net/images/${animation.filename.replace(
              'webm',
              'mp4'
            )}`}
          />
        )}
      </video>
      {previewImage && (
        <img
          alt={previewImage.title}
          className={previewImageClasses}
          data-testid='video-animation-previewImg'
          onClick={showPlayButton ? videoPlayToggle : videoPlay}
          onMouseEnter={!showPlayButton ? videoPlay : null}
          src={previewImage.url}
        />
      )}
    </div>
  )
}

export default VideoAnimation
